(() => {
  const config = window.themeEntranceAnimations || {};

  const enabled = Boolean(config.enabled);
  const respectReducedMotion = config.respectReducedMotion !== false;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!enabled || (respectReducedMotion && prefersReducedMotion)) {
    return;
  }

  const durationMs = Math.max(120, Number(config.duration) || 700);
  const durationSeconds = durationMs / 1000;
  const distance = Math.max(0, Number(config.distance) || 28);
  const staggerMs = Math.max(0, Number(config.stagger) || 70);
  const triggerMargin = Math.max(0, Math.min(60, Number(config.triggerMargin) || 14));

  const hasGsap = Boolean(window.gsap);
  const prepared = new WeakSet();
  const animated = new WeakSet();
  const groupedPrepared = new WeakSet();
  const groupedAnimated = new WeakSet();
  const groupedTargets = new WeakMap();

  const selectors = {
    heading: 'h1, h2, h3, h4, h5, h6',
    body:
      'p, li, blockquote, figcaption, [class*="rte"] p, [class*="rte"] li, .text-subtext, .text-accent',
    image: 'img, picture, video',
    button: 'a.button, button, input[type="submit"], input[type="button"]',
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const element = entry.target;
        observer.unobserve(element);

        if (animated.has(element)) return;
        animated.add(element);

        const animationType = element.dataset.entranceType || 'none';
        const delay = Number(element.dataset.entranceDelay || 0);

        if (animationType === 'none') return;

        if (animationType.startsWith('css_')) {
          element.classList.add('theme-entrance-visible');
          return;
        }

        runGsapAnimation(element, animationType, delay);
      });
    },
    {
      threshold: 0.15,
      rootMargin: `0px 0px -${triggerMargin}% 0px`,
    },
  );

  const groupedObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const element = entry.target;
        groupedObserver.unobserve(element);

        if (groupedAnimated.has(element)) return;
        groupedAnimated.add(element);

        const groupState = groupedTargets.get(element);
        if (!groupState || groupState.animationType === 'none') return;

        if (groupState.animationType.startsWith('css_')) {
          groupState.targets.forEach((target) => target.classList.add('theme-entrance-visible'));
          return;
        }

        runGroupedGsapAnimation(groupState.targets, groupState.animationType);
      });
    },
    {
      threshold: 0.15,
      rootMargin: `0px 0px -${triggerMargin}% 0px`,
    },
  );

  function resolveAnimationType(group) {
    const selected = config[group];
    return selected || 'none';
  }

  function resolveAnimationTypeForElement(groupName, element) {
    if (groupName === 'button' && element?.matches?.('[data-basic-content-text-cta]')) {
      return resolveAnimationType('body');
    }

    return resolveAnimationType(groupName);
  }

  function shouldSkipElement(element) {
    if (!element || prepared.has(element)) return true;
    if (element.closest('[data-no-entrance], [hidden], dialog, details:not([open])')) return true;
    if (element.closest('[data-card-entrance]')) return true;
    if (element.classList.contains('header__logo-image')) return true;
    return false;
  }

  function prepareAnimation(element, animationType, delay) {
    if (animationType.startsWith('css_')) {
      prepareCssAnimation(element, animationType, delay);
      return animationType;
    }

    const gsapPrepared = prepareGsapAnimation(element, animationType);
    if (!gsapPrepared) {
      element.dataset.entranceType = 'css_fade_in';
      prepareCssAnimation(element, 'css_fade_in', delay);
      return 'css_fade_in';
    }

    return animationType;
  }

  function prepareCssAnimation(element, animationType, delay) {
    element.classList.add('theme-entrance-pending', `theme-entrance-${animationType}`);
    element.style.setProperty('--theme-entrance-duration', `${durationMs}ms`);
    element.style.setProperty('--theme-entrance-delay', `${delay}ms`);
    element.style.setProperty('--theme-entrance-distance', `${distance}px`);
  }

  function prepareGsapAnimation(element, animationType) {
    if (!hasGsap) return false;

    const gsap = window.gsap;

    if (animationType === 'gsap_fade_up') {
      gsap.set(element, { autoAlpha: 0, y: distance });
      return true;
    }

    if (animationType === 'gsap_blur_up') {
      gsap.set(element, { autoAlpha: 0, y: distance, filter: 'blur(10px)' });
      return true;
    }

    if (animationType === 'gsap_scale_fade') {
      gsap.set(element, { autoAlpha: 0, scale: 0.92, y: Math.round(distance * 0.35) });
      return true;
    }

    if (animationType === 'gsap_pop_in') {
      gsap.set(element, { autoAlpha: 0, scale: 0.9, y: Math.round(distance * 0.2) });
      return true;
    }

    return false;
  }

  function runGsapAnimation(element, animationType, delay) {
    if (!hasGsap) {
      element.classList.add('theme-entrance-visible');
      return;
    }

    const gsap = window.gsap;
    const easingMap = {
      gsap_fade_up: 'power3.out',
      gsap_blur_up: 'power3.out',
      gsap_scale_fade: 'power2.out',
      gsap_pop_in: 'back.out(1.2)',
    };

    const tweenVars = {
      duration: durationSeconds,
      delay: delay / 1000,
      autoAlpha: 1,
      y: 0,
      scale: 1,
      filter: 'blur(0px)',
      ease: easingMap[animationType] || 'power3.out',
      clearProps: 'transform,filter,opacity,visibility',
    };

    gsap.to(element, tweenVars);
  }

  function runGroupedGsapAnimation(targets, animationType) {
    if (!hasGsap) {
      targets.forEach((target) => target.classList.add('theme-entrance-visible'));
      return;
    }

    const gsap = window.gsap;
    const easingMap = {
      gsap_fade_up: 'power3.out',
      gsap_blur_up: 'power3.out',
      gsap_scale_fade: 'power2.out',
      gsap_pop_in: 'back.out(1.2)',
    };

    gsap.to(targets, {
      duration: durationSeconds,
      autoAlpha: 1,
      y: 0,
      scale: 1,
      filter: 'blur(0px)',
      ease: easingMap[animationType] || 'power3.out',
      clearProps: 'transform,filter,opacity,visibility',
      stagger: 0,
    });
  }

  function resolveCardAnimationType() {
    const orderedGroups = ['image', 'heading', 'body', 'button'];
    for (const groupName of orderedGroups) {
      const animationType = resolveAnimationType(groupName);
      if (animationType !== 'none') return animationType;
    }

    return 'none';
  }

  function shouldSkipCardGroup(groupRoot) {
    if (!groupRoot) return true;

    if (groupRoot.matches?.('[data-card-entrance="product"]')) {
      if (
        groupRoot.closest(
          '.collection-template--bronze, .featured-collection--bronze, .recommended-products--bronze'
        )
      ) {
        return true;
      }
    }

    return false;
  }

  function setupCardGroups(root = document) {
    const groups = [
      {
        selector: '[data-card-entrance="product"]',
        targets: '.product-card__media, .product-card__content',
      },
      {
        selector: '[data-card-entrance="archive"]',
        targets: '.blog-archive__media, .blog-archive__overlay, .featured-archive__media, .featured-archive__overlay',
      },
    ];

    groups.forEach(({ selector, targets }) => {
      root.querySelectorAll(selector).forEach((groupRoot) => {
        if (!groupRoot || groupedPrepared.has(groupRoot)) return;
        if (groupRoot.closest('[data-no-entrance], [hidden], dialog, details:not([open])')) return;
        if (shouldSkipCardGroup(groupRoot)) return;

        const groupElements = Array.from(groupRoot.querySelectorAll(targets)).filter(
          (target) => !target.closest('[hidden], dialog, details:not([open])')
        );

        if (!groupElements.length) return;

        let animationType = resolveCardAnimationType();
        groupElements.forEach((target) => {
          animationType = prepareAnimation(target, animationType, 0);
        });

        groupedTargets.set(groupRoot, {
          targets: groupElements,
          animationType,
        });
        groupedPrepared.add(groupRoot);
        groupedObserver.observe(groupRoot);
      });
    });
  }

  function setupGroup(groupName, root = document) {
    const elements = root.querySelectorAll(selectors[groupName]);
    let index = 0;

    elements.forEach((element) => {
      if (shouldSkipElement(element)) return;

      const animationType = resolveAnimationTypeForElement(groupName, element);
      if (animationType === 'none') return;

      const delay = Math.min(index * staggerMs, 600);
      element.dataset.entranceType = animationType;
      element.dataset.entranceDelay = String(delay);
      prepared.add(element);
      index += 1;

      prepareAnimation(element, animationType, delay);

      observer.observe(element);
    });
  }

  function init(root = document) {
    setupCardGroups(root);
    setupGroup('heading', root);
    setupGroup('body', root);
    setupGroup('image', root);
    setupGroup('button', root);
  }

  const revealPendingElements = (root = document) => {
    const elements = root.querySelectorAll('.theme-entrance-pending:not(.theme-entrance-visible)');
    elements.forEach((element) => {
      element.classList.add('theme-entrance-visible');
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // Fail-safe: never let sections stay hidden if observers/triggers miss on desktop.
  window.setTimeout(() => {
    revealPendingElements(document);
  }, 1800);

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    window.setTimeout(() => {
      revealPendingElements(event.target);
    }, 1800);
  });
})();
